import { CONNECTION_DROPPED, UPLOAD_DIDNT_GO_THROUGH } from "./upload-failure";

/**
 * PUT a file to a presigned URL, reporting progress.
 *
 * `XMLHttpRequest` rather than `fetch`: only XHR exposes `upload.onprogress`,
 * and without it a large file is an indeterminate spinner for a minute.
 *
 * No `authorization` header — the URL carries its own signature, and sending
 * our bearer token to S3 would leak it to a third party.
 *
 * Shared by project documents and crew certificates. It lived in
 * `features/project/documents-api.ts` until the crew needed the same three-call
 * upload; a second copy would have been a second place for the missing-token
 * rule above to be forgotten.
 */
export function putWithProgress(
  url: string,
  file: File,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("content-type", file.type || "application/octet-stream");

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    // Sentences a person can act on (`architecture/0031`). The status stays in
    // the console: "Upload failed (403)" told nobody that the link had expired
    // and a Retry would fetch a new one.
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      console.warn(`Storage answered the upload with ${xhr.status}.`);
      reject(new Error(UPLOAD_DIDNT_GO_THROUGH));
    };
    xhr.onerror = () => reject(new Error(CONNECTION_DROPPED));
    xhr.onabort = () => reject(new Error("Upload cancelled."));

    xhr.send(file);
  });
}
